import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FiMail, FiArrowLeft, FiCheck } from 'react-icons/fi';
import { authAPI } from '../services/api';
import Footer from '../components/Footer';
import theme from '../styles/theme';

// Schema de validação
const recuperarSenhaSchema = yup.object({
  email: yup
    .string()
    .email('E-mail inválido')
    .required('E-mail é obrigatório'),
});

// Componentes estilizados
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};
`;

const Card = styled.div`
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius['2xl']};
  box-shadow: ${theme.shadows['2xl']};
  padding: ${theme.spacing[8]};
  width: 100%;
  max-width: 450px;
  border: 1px solid ${theme.colors.border.primary};
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[2]} 0;
  text-align: center;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  text-align: center;
  margin: 0 0 ${theme.spacing[8]} 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[10]} ${theme.spacing[3]} ${theme.spacing[10]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Icon = styled.div`
  position: absolute;
  left: ${theme.spacing[3]};
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.lg};
  z-index: 1;
`;

const ErrorMessage = styled.span`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.sm};
  margin-top: ${theme.spacing[1]};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.success[600]};
  font-size: ${theme.fontSizes.sm};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: ${theme.spacing[3]};
  background: ${theme.colors.primary[600]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[700]};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary[500]};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
  padding: 0;
  margin-top: ${theme.spacing[4]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.colors.primary[600]};
  }

  &:focus {
    outline: none;
  }
`;

const InfoBox = styled.div`
  background: ${theme.colors.primary[500]}10;
  border: 1px solid ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const RecuperarSenha = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(recuperarSenhaSchema),
  });

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const response = await authAPI.solicitarRecuperacaoSenha(data.email);

      if (response.data.success) {
        setSuccess(true);
      } else {
        setError(response.data.error || 'Erro ao solicitar recuperação de senha');
      }
    } catch (err) {
      console.error('Erro ao solicitar recuperação de senha:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Erro ao conectar com o servidor');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <ContentWrapper>
        <Card>
          <Title>Recuperar Senha</Title>
          <Subtitle>
            Informe seu email para receber o link de redefinição de senha
          </Subtitle>

          {success ? (
            <SuccessMessage>
              <FiCheck />
              Se o email informado existir no sistema, você receberá um link para redefinir sua senha.
            </SuccessMessage>
          ) : (
            <Form onSubmit={handleSubmit(onSubmit)}>
              {error && <ErrorMessage>{error}</ErrorMessage>}

              <InfoBox>
                Digite o email cadastrado no sistema. Se o email existir, você receberá um link para criar uma nova senha.
              </InfoBox>

              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <InputContainer>
                  <Icon>
                    <FiMail />
                  </Icon>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    {...register('email')}
                    disabled={loading}
                  />
                </InputContainer>
                {errors.email && (
                  <ErrorMessage>{errors.email.message}</ErrorMessage>
                )}
              </FormGroup>

              <SubmitButton type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <LoadingSpinner />
                    Enviando...
                  </>
                ) : (
                  'Enviar Link de Recuperação'
                )}
              </SubmitButton>
            </Form>
          )}

          <BackButton onClick={() => navigate('/login')}>
            <FiArrowLeft />
            Voltar para o login
          </BackButton>
        </Card>
      </ContentWrapper>
      <Footer />
    </Container>
  );
};

export default RecuperarSenha;

